import { AddIcon } from "@minket/brand/icons/add";
import { WebhooksIcon } from "@minket/brand/icons/webhooks";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { StatusBadge } from "@minket/ui/components/status-badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@minket/ui/components/table";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { stamp } from "@/components/domains/format";
import { UnavailableAction } from "@/components/page/action";
import { FadeText } from "@/components/page/fade-text";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { TestModeStamp } from "@/components/page/stamp";
import { QueryView } from "@/components/page/states";
import { EndpointDetail } from "@/components/webhooks/detail";
import { useWebhookDialogs } from "@/components/webhooks/host";
import {
  describeEvents,
  type Endpoint,
  endpointStatus,
  endpointTitle,
} from "@/components/webhooks/model";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/webhooks")({
  component: Webhooks,
});

const columns = ["Endpoint", "Status", "Events", "Last 24 hours", "Last answer"] as const;
const will = "Each endpoint will be listed here with its status, its events and its deliveries.";

/** One sentence: how the endpoints stand. */
function standing(items: readonly Endpoint[]): string {
  const off = items.filter((item) => item.status === "auto_disabled").length;
  const count = items.length === 1 ? "One endpoint" : `${items.length} endpoints`;
  if (off > 0)
    return `${count}; ${off === 1 ? "one was" : `${off} were`} disabled after 3 days of failures.`;
  return `${count}. Each event is posted as JSON with svix signatures, retried for 3 days.`;
}

function Webhooks() {
  const { project } = useWorkspace();
  const projectId = project?.id ?? "";
  const list = useQuery({ ...apiQuery(endpoints.webhooks, { projectId }), enabled: !!project });
  const dialogs = useWebhookDialogs();
  const canManage = useCan("webhooks.manage");
  const preview = (busy: boolean) => (
    <GhostTable columns={columns} label="webhook endpoints" busy={busy} />
  );

  return (
    <Page
      title="Webhooks"
      description={list.data ? standing(list.data.data) : undefined}
      action={
        !project || !canManage ? (
          <UnavailableAction
            label="Add endpoint"
            icon={AddIcon}
            reason={project ? "Viewers cannot add endpoints" : "Pick a project first"}
          />
        ) : (
          <Button onClick={() => dialogs.open({ kind: "create", projectId })}>
            <AddIcon className="size-5" />
            Add endpoint
          </Button>
        )
      }
    >
      <ProjectScope will={will} icon={<WebhooksIcon />} preview={preview}>
        {(current) => (
          <QueryView query={list} noun="webhook endpoints" preview={preview}>
            {(page) =>
              page.data.length === 0 ? (
                <EmptyState
                  data-state="empty"
                  icon={<WebhooksIcon />}
                  title="No endpoints yet"
                  description="Add an https endpoint and choose its events; every delivery is signed and retried for 3 days."
                  preview={preview(false)}
                />
              ) : (
                <Endpoints
                  projectId={current.id}
                  items={page.data}
                  canManage={canManage}
                  open={dialogs.open}
                />
              )
            }
          </QueryView>
        )}
      </ProjectScope>
      {dialogs.element}
    </Page>
  );
}

/**
 * The endpoints ledger (direction §7): each endpoint named by its description or its URL's host,
 * its URL whole in its name and tooltip, its state, its events and its day in figures. Choosing
 * one opens it under the ledger.
 */
function Endpoints({
  projectId,
  items,
  canManage,
  open,
}: {
  projectId: string;
  items: Endpoint[];
  canManage: boolean;
  open: ReturnType<typeof useWebhookDialogs>["open"];
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selected = items.find((item) => item.id === selectedId) ?? items[0];
  return (
    <div className="grid min-w-0 gap-8">
      <Table aria-label="Webhook endpoints of this project" className="min-w-[44rem]">
        <TableHeader>
          <TableRow>
            {columns.map((column, index) => (
              <TableHead key={column} className={index === columns.length - 1 ? "text-right" : ""}>
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((item) => {
            const status = endpointStatus[item.status];
            const current = item.id === selected?.id;
            const last = item.stats.last_success_at ?? item.stats.last_failure_at;
            return (
              <TableRow
                key={item.id}
                data-state={current ? "selected" : undefined}
                className="[&>td]:align-middle"
              >
                <TableCell className="max-w-0 min-w-64">
                  <button
                    type="button"
                    aria-pressed={current}
                    onClick={() => setSelectedId(item.id)}
                    className="block w-full min-w-0 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                  >
                    <span className="flex items-center gap-2.5 font-semibold">
                      <FadeText>{endpointTitle(item)}</FadeText>
                      {item.mode === "test" ? <TestModeStamp /> : null}
                    </span>
                    <FadeText className="type-data text-[12.5px] text-muted-foreground">
                      {item.url}
                    </FadeText>
                  </button>
                </TableCell>
                <TableCell>
                  <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                </TableCell>
                <TableCell className="whitespace-nowrap">{describeEvents(item.events)}</TableCell>
                <TableCell className="type-data whitespace-nowrap">
                  {item.stats.delivered_24h}
                  <span className="text-muted-foreground"> delivered </span>
                  <span className={item.stats.failed_24h > 0 ? "text-destructive" : ""}>
                    {item.stats.failed_24h}
                  </span>
                  <span className="text-muted-foreground"> failed</span>
                </TableCell>
                <TableCell className="type-data text-right whitespace-nowrap text-muted-foreground">
                  {last ? <time dateTime={last}>{stamp(last)}</time> : "Never"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {selected ? (
        <EndpointDetail
          key={selected.id}
          projectId={projectId}
          endpoint={selected}
          canManage={canManage}
          open={open}
          onDeleted={() => setSelectedId(null)}
        />
      ) : null}
    </div>
  );
}
